import FormTemplate from '../../html/components/form.html'
import FormFieldTemplate from '../../html/components/form-field.html'
import FormLabelTemplate from '../../html/components/form-label.html'
import FormDescriptionTemplate from '../../html/components/form-description.html'
import FormActionsTemplate from '../../html/components/form-actions.html'
import FormControlTemplate from '../../html/components/form-control.html'

import Component from '../shared/component'
import Relation, {HasOne, HasMany, MemberOf} from '../shared/mixins/relation'

const formFields = Relation()
const fieldLabel = Relation()
const fieldControl = Relation()
const controlInput = Relation()
const controlDescription = Relation()

export const Form = Component('v-form', {
  model: {
    prop: 'value',
    event: 'change'
  },
  mixins: [
    HasMany(formFields, 'fields')
  ],
  template: FormTemplate,
  props: {
    value: {
      type: Object,
      default () {
        return {}
      }
    }
  },
  data () {
    return {
      active: this.value
    }
  },
  computed: {
    inputs () {
      return this.fields
        .filter(field => field.control && field.control.input)
        .map(field => field.control.input)
    }
  },
  watch: {
    value: {
      deep: true,
      handler () {
        this.active = this.value
      }
    },

    active: {
      deep: true,
      handler () {
        this.$emit('change', this.active)

        this.inputs.forEach(input => {
          if (input.name in this.active) {
            input.change(this.active[input.name])
          } else {
            input.change(null)
          }
        })
      }
    },

    inputs () {
      this.inputs.forEach(input => {
        if (input.name in this.active) {
          input.change(this.active[input.name])
        } else {
          input.change(null)
        }

        input.$on('change', value => {
          this.$set(this.active, input.name, value)
        })
      })
    }
  },
  methods: {
    submit () {
      this.$emit('submit', this.active)
    }
  }
})

export const FormField = Component('v-form-field', {
  mixins: [
    HasOne(fieldLabel, 'label'),
    HasOne(fieldControl, 'control'),
    MemberOf(formFields, 'form')
  ],
  template: FormFieldTemplate,
  props: {
    inline: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    componentClass () {
      const {control} = this

      if (!control) {
        return
      }

      const {input} = control

      if (input) {
        return `has-${input.component}`
      }
    }
  }
})

export const FormLabel = Component('v-form-label', {
  mixins: [
    MemberOf(fieldLabel, 'field')
  ],
  template: FormLabelTemplate,
  computed: {
    input () {
      const {field} = this

      if (!field) {
        return
      }

      const {control} = field

      if (!control) {
        return
      }

      const {input} = control

      if (input) {
        return input.id
      }
    }
  }
})

export const FormDescription = Component('v-form-description', {
  mixins: [
    MemberOf(controlDescription, 'control')
  ],
  template: FormDescriptionTemplate
})

export const FormActions = Component('v-form-actions', {
  template: FormActionsTemplate,
  props: {
    align: {
      type: String,
      default: null,
      validator (value) {
        switch (value) {
          case '':
          case 'right':
          case 'left':
            return true
          default:
            return false
        }
      }
    }
  },
  computed: {
    alignClass () {
      return this.align && `is-${this.align}`
    }
  }
})

export const FormControl = Component('v-form-control', {
  mixins: [
    HasOne(controlInput, 'input'),
    HasOne(controlDescription, 'description'),
    MemberOf(fieldControl, 'field')
  ],
  template: FormControlTemplate
})

export const FormControlMixin = component => ({
  model: {
    prop: 'value',
    event: 'change'
  },
  mixins: [
    MemberOf(controlInput, 'control')
  ],
  props: {
    value: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      active: this.value,
      component
    }
  },
  computed: {
    label () {
      const {control} = this

      if (!control) {
        return
      }

      const {field} = control

      if (!field) {
        return
      }

      const {label} = field

      if (label) {
        return label.id
      }
    },

    description () {
      const {control} = this

      if (!control) {
        return
      }

      const {description} = control

      if (description) {
        return description.id
      }
    }
  },
  watch: {
    value () {
      this.change(this.value)
    },

    active () {
      this.$emit('change', this.active)
    }
  },
  methods: {
    change (value) {
      if (value === null) {
        const {options} = this.constructor
        const {value} = options.props

        if (typeof value.default === 'function') {
          this.active = value.default()
        } else {
          this.active = value.default
        }
      } else {
        this.active = value
      }
    },

    submit () {
      const {control} = this

      if (!control) {
        return
      }

      const {field} = control

      if (!field) {
        return
      }

      const {form} = field

      if (form) {
        form.submit()
      }
    }
  }
})
